import React, { useState, useEffect } from 'react';
import { Box, Alert, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { saturationAPI } from '../services/api';

import {
  DashboardHeader,
  KeyMetricsCards,
  MarketOverview,
  SectorHeatmap,
  SectorDetail,
  getSaturationColor,
  SaturationDashboardSkeleton
} from '../components/saturation-dashboard';

function SaturationDashboard() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [allSectors, setAllSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [summaryRes, sectorsRes] = await Promise.all([
        saturationAPI.getSummary(),
        saturationAPI.getAllSectors()
      ]);
      setSummary(summaryRes.data.data);
      setAllSectors(sectorsRes.data.data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSectorClick = async (sector) => {
    try {
      // sector.sector may be {en, vi} object or a plain string
      const sectorName = typeof sector.sector === 'object'
        ? sector.sector.en
        : sector.sector;
      const response = await saturationAPI.getSector(sectorName);
      setSelectedSector(response.data.data);
    } catch (err) {
      console.error('Error fetching sector details:', err);
    }
  };

  const getSaturationColorWithTranslation = (score) => {
    const result = getSaturationColor(score);
    return { ...result, text: result.textKey ? t(result.textKey) : result.text };
  };

  if (loading) return <SaturationDashboardSkeleton />;

  if (error) {
    return (
      <Box sx={{ width: '100%', px: 3, py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('saturation.errorLoading', 'Lỗi tải dữ liệu')}: {error}
        </Alert>
        <Box display="flex" justifyContent="center">
          <IconButton onClick={fetchData} color="primary"><RefreshIcon /></IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: 0, py: { xs: 1, sm: 2 } }}>
      <DashboardHeader loading={loading} onRefresh={fetchData} />
      <KeyMetricsCards summary={summary} />

      {/* Main two-column content */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 2.5 } }}>
        {/* Left: Sector Rankings */}
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(58% - 10px)' }, minWidth: 0 }}>
          <SectorHeatmap
            allSectors={allSectors}
            onSectorClick={handleSectorClick}
            getSaturationColor={getSaturationColorWithTranslation}
          />
        </Box>

        {/* Right: Distribution + Guide */}
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(42% - 10px)' }, minWidth: 0 }}>
          <MarketOverview summary={summary} />
        </Box>
      </Box>

      <SectorDetail
        key={selectedSector?.sector_code || 'no-sector'}
        selectedSector={selectedSector}
        onClose={() => setSelectedSector(null)}
        getSaturationColor={getSaturationColorWithTranslation}
      />
    </Box>
  );
}

export default SaturationDashboard;
